import React, { useEffect, useMemo, useState } from "react";
import api, { deactivateEntityClient } from "../services/api";
import ManagementList from "../components/ManagementList";
import { formatDate } from "../lib/utils/helper";
import { LIMIT } from "../lib/utils/constant";
import { usePermissions } from "../Hooks/usePermission";
import { PERMISSIONS } from "../lib/utils/permissions";
import PermissionMessage from "../components/ErrorMessage/PermissionMessage";

const ClientManagement = () => {
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [totalPages, setTotalPages] = useState(1);
  const [counts, setCounts] = useState(null);
  const [page, setPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(LIMIT[0].value)
  const [statusFilter, setStatusFilter] = useState('A');
  const { can } = usePermissions();
  const userRoleCode = JSON.parse(localStorage.getItem('user'))?.role?.code;
  const canViewClient = can(PERMISSIONS.CLIENT_READ, userRoleCode);
  const canCreateClient = can(PERMISSIONS.CLIENT_CREATE,userRoleCode);
  const canUpdateClient = can(PERMISSIONS.CLIENT_UPDATE,userRoleCode);
  const canDeactivateClient = can(PERMISSIONS.CLIENT_DEACTIVATE,userRoleCode);

  const formatUpdatedBy = (updatedBy) => {
    if (!updatedBy) return "-";
    if (typeof updatedBy === "string" || typeof updatedBy === "number") return String(updatedBy);
    if (typeof updatedBy === "object") {
      const first = updatedBy?.firstName ? String(updatedBy.firstName) : "";
      const last = updatedBy?.lastName ? String(updatedBy.lastName) : "";
      const full = `${first} ${last}`.trim();
      if (full) return full;
      if (updatedBy?.email) return String(updatedBy.email);
      if (updatedBy?.username) return String(updatedBy.username);
    }
    return "-";
  };

  const columns = useMemo(
    () => [
      { header: "CLIENT", accessor: "name", align: "center" },
      {
        header: "ENTITY",
        accessor: "parentEntityName",
        align: "center",
      },
      { header: "ENROLLED PATIENTS", accessor: "enrolledPatients", align: "center" },
      { header: "LAST UPDATED", accessor: "updatedAtDisplay", align: "center" },
      { header: "UPDATED BY", accessor: "updatedByDisplay", align: "center" },
      { header: "ACTION", accessor: "action", align: "center" },
    ],
    []
  );

  const handleLimitChange = (e) => {
    setItemsPerPage(e.target.value)
  }

  // 4. Entity Management
  const fetchClientLists = async () => {
    const payload = {
      start: null,
      end: null,
      page: page,
      limit: itemsPerPage,
      status: statusFilter,
    };
    try {
      setLoading(true);
      const response = await api.post("/v1/entities/clients/list", payload);
      const data = response?.data?.data;
      const resolvedTotalPages = Number(data?.pagination?.pages) || 1;
      setTotalPages(resolvedTotalPages);
      setClients(data);
      setCounts(data?.counts || data?.statusCounts || null);
    } catch (error) {
      console.error("Error fetching entity lists:", error);
    } finally {
      setLoading(false);
    }
  };

  const filteredClients = (clients?.items || [])
    .map((client) => {
      return {
        ...client,
        parentEntityName: client?.parentEntity?.name || "-",
        enrolledPatients: client?.customerCount || 0,
        updatedAtDisplay: formatDate(client?.updatedAt) || "-",
        updatedByDisplay: formatUpdatedBy(client?.updatedBy),
      };
    })
    .filter((entity) => {
      const clients = Array.isArray(entity?.clients) ? entity.clients : [];
      if (!searchTerm) return true;

      const lowerTerm = searchTerm.toLowerCase();

      const matchesEntity =
        (entity?.name && String(entity.name).toLowerCase().includes(lowerTerm)) ||
        (entity?.abbreviation &&
          String(entity.abbreviation).toLowerCase().includes(lowerTerm)) ||
        (entity?.parentEntityName &&
          String(entity.parentEntityName).toLowerCase().includes(lowerTerm));

      const matchesClients = clients.some((c) => {
        const n = c?.name ? String(c.name).toLowerCase() : "";
        const a = c?.abbreviation ? String(c.abbreviation).toLowerCase() : "";
        const p = c?.parentEntityName ? String(c.parentEntityName).toLowerCase() : "";
        return n.includes(lowerTerm) || a.includes(lowerTerm) || p.includes(lowerTerm);
      });

      return matchesEntity || matchesClients;
    });
  const handleTabChange = (tabKey) => {
    let apiStatus = 'A';
    if (tabKey === 'active') apiStatus = 'A';
    if (tabKey === 'pending') apiStatus = 'DF';
    if (tabKey === 'deactivated') apiStatus = 'D';
    if (tabKey === 'all') apiStatus = 'ALL';

    setStatusFilter(apiStatus);
    setPage(1);
  };

  const handleSearch = (term) => {
    setSearchTerm(term);
    // No need to reset page for client-side filtering of current page data
  };
  const handleDeactivate = async (id, reason) => {
    try {
      await deactivateEntityClient(id, reason);
      fetchClientLists();
    } catch (err) {
      console.error("Error deactivating client:", err);
    }
  };
  useEffect(() => {
    fetchClientLists();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, statusFilter, itemsPerPage]);

  const isDeactivateDisabled = (entity) => entity.customerCount > 0;

  return (
    <>
      {canViewClient ? <ManagementList
        title="Clients"
        data={filteredClients}
        columns={columns}
        onDeactivate={handleDeactivate}
        disabled={isDeactivateDisabled}
        addNewLink="/client/new"
        editLinkBase="/client/edit"
        isServerSide={true}
        isLoading={loading}
        serverPage={page}
        serverTotalPages={totalPages}
        serverCounts={counts}
        onPageChange={(newPage) => setPage(newPage)}
        onTabChange={handleTabChange}
        hiddenTabs={["pending"]}
        onSearch={handleSearch}
        limit={itemsPerPage}
        handleLimitChange={handleLimitChange}
        deactivationType="client"
        canCreate={canCreateClient}
        canUpdate={canUpdateClient}
        canDeactivate={canDeactivateClient}
        isAdminDeactivateDisabled={()=>{return false}}
      /> : <PermissionMessage  />}
    </>
  );
};

export default ClientManagement;